import { useEffect, useMemo, useState } from "react";
import {
  ChartTooltipContent,
  cn,
  GappedChart,
  TabsFluid,
} from "web-design-system";
import { formatAsPercentage, withScaledBarPct } from "creso-core/utils";
import { AllocationBarRow } from "web-core";
import { AssetAllocationBucket } from "creso-core/types";
import { OverviewSection } from "./overview-section";

export const ASSET_ALLOCATION_BUCKET_COLORS: Record<string, string> = {
  indian_equity: "var(--color-chart-1)",
  debt: "var(--color-chart-2)",
  commodity: "var(--color-chart-3)",
  global: "var(--color-chart-4)",
};

type AssetAllocationProps = {
  buckets: AssetAllocationBucket[];
  label?: string;
  defaultOpen?: boolean;
};

export function AssetAllocation({
  buckets,
  label = "Asset Allocation",
  defaultOpen = true,
}: AssetAllocationProps) {
  const [activeBucketKey, setActiveBucketKey] = useState(() => buckets[0]?.key);
  const activeBucket =
    buckets.find((bucket) => bucket.key === activeBucketKey) ?? buckets[0];

  useEffect(() => {
    if (!buckets.some((bucket) => bucket.key === activeBucketKey)) {
      setActiveBucketKey(buckets[0]?.key);
    }
  }, [buckets, activeBucketKey]);

  const allocationTabs = useMemo(
    () =>
      buckets.map((bucket) => ({
        value: bucket.key,
        title: (
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded shrink-0"
              style={{ backgroundColor: bucket.color }}
            />
            <span className="font-semibold">{bucket.label}</span>
            <span className="text-muted-foreground">
              {formatAsPercentage(bucket.pct, "-", 1)}
            </span>
          </div>
        ),
        body: null,
      })),
    [buckets]
  );

  const activeBucketRows = useMemo(
    () => withScaledBarPct(activeBucket?.breakdown ?? []),
    [activeBucket]
  );

  // Rank each row by pct (0 = highest) rather than relying on array
  // position, so opacity steps stay evenly spaced even when pct values
  // are close together.
  const activeBucketRowRanks = useMemo(
    () =>
      activeBucketRows.map(
        (row) => activeBucketRows.filter((other) => other.pct > row.pct).length
      ),
    [activeBucketRows]
  );

  const segments = useMemo(
    () =>
      buckets.map((bucket) => ({
        key: bucket.key,
        value: bucket.pct,
        color: bucket.color,
        label: bucket.label,
      })),
    [buckets]
  );

  if (!buckets.length) return null;

  const content = (
    <div className="flex flex-col bg-inherit gap-4">
      <GappedChart
        segments={segments}
        className="h-7 w-full"
        tooltipContent={
          <ChartTooltipContent
            indicator="dot"
            labelFormatter={() => "Asset Allocation"}
            valueFormatter={(val) => formatAsPercentage(val as string, "-", 2)}
          />
        }
      />
      <div className="flex flex-col bg-inherit gap-px">
        <TabsFluid
          tabs={allocationTabs}
          value={activeBucketKey}
          onValueChange={setActiveBucketKey}
          contentClassName="hidden"
        />
        <div
          className={cn(
            "flex flex-col gap-4 -mt-px bg-background rounded-lg border border-border p-4",
            activeBucket?.key === buckets[0]?.key && "rounded-tl-none"
          )}
        >
          {activeBucketRows.length > 0 && (
            <span className="text-sm text-muted-foreground">
              Breakdown by{" "}
              <span className="text-foreground font-medium">
                {activeBucket.breakdownBy}
              </span>
            </span>
          )}
          {activeBucketRows.map((row, index) => (
            <AllocationBarRow
              key={`${row.label}-${index}`}
              label={row.label}
              pct={row.pct}
              barPct={row.barPct}
              opacity={
                activeBucketRows.length > 1
                  ? 1 -
                    (activeBucketRowRanks[index] /
                      (activeBucketRows.length - 1)) *
                      0.5
                  : 1
              }
              color={ASSET_ALLOCATION_BUCKET_COLORS[activeBucket.key]}
            />
          ))}
          {activeBucketRows.length === 0 ? (
            <div className="flex min-h-48 items-center justify-center text-sm w-full text-center text-muted-foreground">
              No further breakdown available
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <OverviewSection defaultOpen={defaultOpen} label={label}>
      {content}
    </OverviewSection>
  );
}
